import { CallStatus } from '../../../types';
import { CallContactEvent } from '../types';
import {
  resolveCallContactFinalState,
  resolveCallContactIsInbound,
  resolveCallContactStatus,
} from './nice-event-parsing.util';

export type MappedCallContactStatus = CallStatus;

export function isOutboundContactEvent(
  callEvent: CallContactEvent,
  statusLower: string,
  currentDirection: 'INBOUND' | 'OUTBOUND' | null
): boolean {
  const isInbound = resolveCallContactIsInbound(callEvent);
  if (isInbound === false) {
    return true;
  }
  if (statusLower.includes('outbound')) {
    return true;
  }
  return currentDirection === 'OUTBOUND';
}

export function isFinalDisconnect(
  callEvent: CallContactEvent,
  _requireDisposition: boolean
): boolean {
  const finalState = resolveCallContactFinalState(callEvent);
  if (finalState === undefined) {
    // requireDisposition is often still false at disconnect time — it is only
    // set when the agent-state ACW event arrives (after disconnect). Treating
    // undefined FinalState as final closes the CXone contact too early and
    // saveDisposition then fails with InvalidContactId.
    // Prefer keeping the contact open until NICE sends FinalState=true.
    return false;
  }
  return finalState;
}

export function mapCallContactStatus(
  callEvent: CallContactEvent,
  currentDirection: 'INBOUND' | 'OUTBOUND' | null
): MappedCallContactStatus {
  const status = resolveCallContactStatus(callEvent);
  const statusLower = status.toLowerCase();

  const isRingingStatus =
    statusLower.includes('inbound') ||
    statusLower.includes('ringing') ||
    statusLower.includes('incoming') ||
    statusLower === 'new';

  const isActiveStatus =
    statusLower.includes('active') ||
    statusLower.includes('connected') ||
    statusLower.includes('joined') ||
    statusLower.includes('masking') ||
    statusLower === 'answered';

  const isHoldingStatus = statusLower.includes('hold');
  const isDisconnectedStatus =
    statusLower.includes('disconnect') ||
    statusLower.includes('ended') ||
    statusLower.includes('closed');

  const isOutbound = isOutboundContactEvent(callEvent, statusLower, currentDirection);
  const isOutboundProgressStatus =
    isOutbound &&
    (statusLower.includes('dialing') ||
      statusLower.includes('dial') ||
      statusLower.includes('ringing') ||
      statusLower.includes('preview') ||
      statusLower.includes('naturalcallamd'));

  if (isDisconnectedStatus) {
    return 'disconnected';
  }
  if (isHoldingStatus) {
    return 'holding';
  }
  if (isActiveStatus) {
    return 'active';
  }
  if (isOutboundProgressStatus) {
    return 'connecting';
  }
  if (isRingingStatus) {
    return 'ringing';
  }

  return 'idle';
}

export function resolveCallStateStatusAfterDisconnect(
  requireDisposition: boolean,
  callEvent: CallContactEvent
): CallStatus {
  const contactStillOpenForAcw = !isFinalDisconnect(callEvent, requireDisposition);
  return contactStillOpenForAcw ? 'disconnected' : 'idle';
}

const CALL_STATUS_RANK: Record<CallStatus, number> = {
  idle: 0,
  connecting: 1,
  ringing: 1,
  active: 2,
  holding: 2,
  disconnected: 3,
};

/** Prevents stale or out-of-order SDK events from downgrading live call UI (e.g. active → connecting). */
export function coalesceCallStatus(previous: CallStatus, next: CallStatus): CallStatus {
  if (next === 'disconnected') {
    return 'disconnected';
  }
  if (previous === 'disconnected') {
    return next === 'idle' ? 'idle' : previous;
  }

  const previousRank = CALL_STATUS_RANK[previous] ?? 0;
  const nextRank = CALL_STATUS_RANK[next] ?? 0;
  if (nextRank < previousRank && previous !== 'idle') {
    return previous;
  }

  return next;
}
